/**
 * 通用节点包装器
 *
 * 提供统一的节点样式、选中状态、执行状态高亮等
 */

import React from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { NodeExecutionStatus } from '../../../types/workflow';

export interface NodeWrapperProps {
  /** 节点 ID */
  nodeId: string;
  /** 节点类型（用于图标和颜色） */
  nodeType: 'start' | 'agent' | 'loop' | 'condition' | 'info' | 'note';
  /** 节点标签 */
  label: string;
  /** 是否选中 */
  isSelected?: boolean;
  /** 执行状态 */
  executionStatus?: NodeExecutionStatus;
  /** 额外的类名 */
  className?: string;
  /** 子元素 */
  children?: React.ReactNode;
  /** 图标 */
  icon?: React.ReactNode;
  /** 是否显示输入连接点 */
  showInputHandle?: boolean;
  /** 是否显示输出连接点 */
  showOutputHandle?: boolean;
  /** 自定义宽度 */
  width?: number | string;
  /** 点击回调 */
  onClick?: () => void;
}

// 节点类型图标
const NodeTypeIcons: Record<string, React.ReactNode> = {
  start: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  agent: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  loop: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  condition: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  info: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  note: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
};

// 节点类型颜色
const NodeTypeColors: Record<string, { bg: string; border: string; text: string }> = {
  start: {
    bg: 'bg-green-50',
    border: 'border-green-300',
    text: 'text-green-600',
  },
  agent: {
    bg: 'bg-paper-50',
    border: 'border-paper-300',
    text: 'text-paper-700',
  },
  loop: {
    bg: 'bg-purple-50',
    border: 'border-purple-300',
    text: 'text-purple-600',
  },
  condition: {
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-600',
  },
  info: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-300',
    text: 'text-cyan-600',
  },
  note: {
    bg: 'bg-gray-100',
    border: 'border-gray-300',
    text: 'text-gray-600',
  },
};

// 执行状态样式
const ExecutionStatusStyles: Record<NodeExecutionStatus, string> = {
  pending: '',
  running: 'border-paper-500 shadow-lg shadow-paper-200 animate-pulse',
  completed: 'border-success-500',
  failed: 'border-error-500',
  skipped: 'border-gray-300 opacity-60',
};

export const NodeWrapper: React.FC<NodeWrapperProps> = React.memo(({
  nodeId,
  nodeType,
  label,
  isSelected = false,
  executionStatus = NodeExecutionStatus.PENDING,
  className,
  children,
  icon,
  showInputHandle = true,
  showOutputHandle = true,
  width = 240,
  onClick,
}) => {
  const colors = NodeTypeColors[nodeType];
  const nodeIcon = icon || NodeTypeIcons[nodeType];

  // 基础样式
  const baseClasses = clsx(
    'rounded-lg border-2 bg-white shadow-sm',
    'transition-all duration-150',
    'hover:shadow-md',
    'cursor-pointer',
    // 选中状态
    isSelected && 'ring-2 ring-paper-500 ring-offset-2',
    // 执行状态
    ExecutionStatusStyles[executionStatus],
  );

  return (
    <div
      className={twMerge(baseClasses, className)}
      style={{ width: typeof width === 'number' ? `${width}px` : width }}
      onClick={onClick}
      data-node-id={nodeId}
      data-node-type={nodeType}
      data-execution-status={executionStatus}
    >
      {/* 节点头部 */}
      <div className={clsx(
        'flex items-center gap-2 px-3 py-2 border-b',
        colors.bg,
        colors.border,
        'rounded-t-md',
      )}>
        <span className={colors.text}>{nodeIcon}</span>
        <span className={clsx('text-sm font-medium', colors.text)}>
          {label}
        </span>
      </div>

      {/* 节点内容 */}
      <div className="px-3 py-2">
        {children}
      </div>

      {/* 连接点占位（由具体节点组件渲染） */}
      {showInputHandle && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2" />
      )}
      {showOutputHandle && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2" />
      )}
    </div>
  );
});

NodeWrapper.displayName = 'NodeWrapper';

export default NodeWrapper;
